import React, { useEffect, useState } from "react";
import { FlightFilters, FlightSearch } from "../../../../../../Components";
import { Button, Loader, SidePaneContent, Toast } from "../../../../../../Components";
import { useSearchParams } from "react-router-dom";
import { apis, get, FLOW_HEADER_NAME, generateTraceId } from "../../../../../../Utils/axios";
import FlightResult from "./Result/domestic";
import Summary from "./Result/Components/summary";
import moment from "moment";
import { useUserPreference } from "../../../../../../Hooks/useUserPreference";
import useMobileCheck from "../../../../../../Hooks/useMobileCheck";
import { FiChevronDown, FiChevronUp, FiEdit2, FiX } from "react-icons/fi";
import { CLASSES } from "../../../../../../Utils/constants";

function RoundSRP() {
	const [searchParams] = useSearchParams();
	const { showSidePane } = useUserPreference();
	const { isMobile } = useMobileCheck();
	const from = searchParams.get("from");
	const to = searchParams.get("to");
	const fromCode = searchParams.get("fromCode");
	const fromCity = searchParams.get("fromCity");
	const toCode = searchParams.get("toCode");
	const toCity = searchParams.get("toCity");
	const date = parseInt(searchParams.get("date"));
	const returnDate = parseInt(searchParams.get("returnDate"));
	const adult = parseInt(searchParams.get("adult")) || 1;
	const child = parseInt(searchParams.get("child")) || 0;
	const infant = parseInt(searchParams.get("infant") || 0);
	const cabinClass = searchParams.get("cabinClass") || "1";
	const cabinClassLabel = CLASSES.find((option) => option.value === cabinClass)?.label || cabinClass;
	const requestId = searchParams.get("requestId") || "";
	const approvalId = searchParams.get("approvalId") || "";
	const optimize = searchParams.get("optimize") || false;
	const [Loading, setLoading] = useState(true);
	const [SelectedFlight, setSelectedFlight] = useState();
	const [ShowFiltersPane, setShowFiltersPane] = useState(false);
	const [ShowSearchForm, setShowSearchForm] = useState(false);
	const [Result, setResult] = useState({
		flights: [],
		journeyKey: "",
		isInternational: false,
	});

	const [Filters, setFilters] = useState({
		maxPrice: 0,
		stops: [],
		airlines: [],
		time: [],
		arrivalTime: [],
		inPolicy: false,
		fullyRefundable: false,
	});

	const [FlowId, setFlowId] = useState("");

	useEffect(() => {
		searchFlights({ fromCode, toCode, date, returnDate, adult, child, infant, cabinClass, requestId, approvalId, optimize });
	}, [fromCode, toCode, date, returnDate, adult, child, infant, cabinClass, requestId, approvalId, optimize]);

	useEffect(() => {
		setSelectedFlight(undefined);
		// Keep side pane closed by default across breakpoints.
		// User explicitly opens filters via the Filter action.
		setShowFiltersPane(false);
		showSidePane(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMobile]);

	const searchFlights = ({ fromCode, toCode, date, returnDate, adult, child, infant, cabinClass, requestId, approvalId, optimize }) => {
		setLoading(true);
		setSelectedFlight();
		const flowId = FlowId || generateTraceId();
		if (!FlowId) {
			setFlowId(flowId);
		}
		if (isMobile) {
			setShowFiltersPane(false);
			showSidePane(false);
		}
		get(
			apis.getRoundFlights,
			{ from: fromCode?.toUpperCase(), to: toCode?.toUpperCase(), date: moment(date).format("DD-MM-YYYY"), returnDate: moment(returnDate).format("DD-MM-YYYY"), adult, child, infant, cabinClass, requestId, approvalId, optimize },
			(r, e) => {
				if (r) {
					setResult(r);
					setLoading(false);
				} else if (e) {
					Toast.handleError(e);
					setLoading(false);
				}
			},
			{
				headers: {
					[FLOW_HEADER_NAME]: flowId,
				},
			}
		);
	};

	const handleSelectFlight = (flight) => {
		setSelectedFlight(flight);
		if (flight) {
			setShowFiltersPane(false);
			showSidePane(true);
		}
	};

	const openFilters = () => {
		setSelectedFlight(undefined);
		setShowFiltersPane(true);
		showSidePane(true);
	};

	const closeSidePane = () => {
		setShowFiltersPane(false);
		setSelectedFlight(undefined);
		showSidePane(false);
	};

	const sidePaneNode = SelectedFlight ? (
		<div className="flex flex-col flex-1 min-h-0 h-full">
			<Summary
				{...{
					approval: Result.approval,
					passengers: { adult, child, infant },
					selectedFlight: SelectedFlight,
					approvalId,
					requestId,
					fromCode,
					toCode,
					date,
					returnDate,
					adult,
					child,
					infant,
					cabinClass,
					flowId: FlowId,
					onClose: closeSidePane,
				}}
			/>
		</div>
	) : ShowFiltersPane ? (
		<div className="flex flex-col h-full min-h-0 overflow-hidden">
			<div className="flex-shrink-0 p-4 pb-3 border-b border-surface bg-canvas">
				<div className="flex items-center justify-between gap-3">
					<h5 className="text-sm font-bold">Filters</h5>
					<Button variant="outlined" className="btn-xs shrink-0" onClick={closeSidePane}>
						<FiX className="mr-1" /> Close
					</Button>
				</div>
			</div>
			<div className="flex-1 min-h-0 p-4 pt-3">
				<FlightFilters currency={Result.currency} flights={Result.flights} filters={Filters} updateFilters={setFilters} showArrivalTimeFilter />
			</div>
		</div>
	) : null;

	return (
		<>
			<Loader loading={Loading} />
			{sidePaneNode ? <SidePaneContent>{sidePaneNode}</SidePaneContent> : null}

			<div className="relative p-3 md:p-4 min-w-0">
				{isMobile ? (
					<div className="mb-3 overflow-hidden border rounded-lg bg-canvas border-surface">
						<div className="flex items-start justify-between gap-2 p-3">
							<div className="min-w-0">
								<div className="text-[11px] text-secondary uppercase tracking-wide">Travel context</div>
								<div className="mt-1 text-sm font-semibold truncate">
									{fromCode?.toUpperCase()} to {toCode?.toUpperCase()}
								</div>
								<div className="mt-1 text-xs text-secondary truncate">
									{moment(date).format("DD MMM")} - {moment(returnDate).format("DD MMM YYYY")} | {adult} Adult
									{child > 0 ? `, ${child} Child` : ""}
									{infant > 0 ? `, ${infant} Infant` : ""} | {cabinClassLabel}
								</div>
							</div>
							<Button variant="outlined" className="btn-xs shrink-0" onClick={() => setShowSearchForm((prev) => !prev)}>
								<FiEdit2 className="mr-1" />
								Modify
								{ShowSearchForm ? <FiChevronUp className="ml-1" /> : <FiChevronDown className="ml-1" />}
							</Button>
						</div>
						{ShowSearchForm && (
							<div className="p-2 border-t border-surface">
								<FlightSearch
									mode={{ label: "Search Round Flights", value: "round" }}
									from={{ airportName: from, airportCode: fromCode, cityName: fromCity }}
									to={{ airportName: to, airportCode: toCode, cityName: toCity }}
									date={date}
									range={{ startDate: date, endDate: returnDate }}
									passengers={{ adult, child, infant }}
									cabinClass={{ value: cabinClass }}
									optimizee={optimize}
									onSearch={() => setShowSearchForm(false)}
								/>
							</div>
						)}
					</div>
				) : (
					<FlightSearch
						mode={{ label: "Search Round Flights", value: "round" }}
						from={{ airportName: from, airportCode: fromCode, cityName: fromCity }}
						to={{ airportName: to, airportCode: toCode, cityName: toCity }}
						date={date}
						range={{ startDate: date, endDate: returnDate }}
						passengers={{ adult, child, infant }}
						cabinClass={{ value: cabinClass }}
						optimizee={optimize}
						onSearch={() => setShowSearchForm(false)}
					/>
				)}
				{Loading ? (
					<div className="w-full mt-20 flex-center">
						<div className="text-center space-y-1">
							<div className="text-sm font-medium">Searching available round-trip flights...</div>
							<div className="text-xs text-secondary">
								We&apos;re checking airlines for {fromCode?.toUpperCase()} to {toCode?.toUpperCase()}. This may take a few seconds.
							</div>
						</div>
					</div>
				) : (
					<FlightResult approval={Result.approval} filters={Filters} flights={Result.flights} onSelect={handleSelectFlight} SelectedFlight={SelectedFlight} onOpenFilters={openFilters} isMobile={isMobile} {...{ fromCode, toCode, date, returnDate, adult, child, infant, cabinClass, requestId, approvalId }} />
				)}
			</div>
		</>
	);
}

export default RoundSRP;
